import { Box } from "@mui/material";
import React, { memo, useEffect } from "react";
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
  ListRowProps,
} from "react-virtualized";

interface CVirtualizedListProps<T> {
  data: T[];
  rowRenderer: (props: ListRowProps) => JSX.Element;
  height?: number;
  width?: number;
  rowHeight?: number;
}

function CVirtualizedListComponet<T>({
  data,
  rowRenderer,
  height,
  width,
  rowHeight,
}: CVirtualizedListProps<T>) {
  const cache = React.useRef(
    new CellMeasurerCache({
      fixedWidth: true,
      defaultHeight: 100,
    })
  );

  useEffect(() => {
    cache.current.clearAll();
  }, [data]);
  return (
    <div>
      {width && height ? (
        <List
          height={height}
          width={width}
          rowCount={data.length}
          rowHeight={rowHeight ?? 50}
          rowRenderer={(props) => rowRenderer(props)}
        />
      ) : (
        <div
          style={{ width: "100%", height: height ? `${height}vh` : "100vh" }}
        >
          <AutoSizer>
            {({ width, height }) => (
              <List
                height={height}
                width={width}
                rowCount={data.length}
                rowHeight={cache.current.rowHeight}
                deferredMeasurementCache={cache.current}
                rowRenderer={(props) => {
                  return (
                    <CellMeasurer
                      key={props.key}
                      cache={cache.current}
                      parent={props.parent}
                      columnIndex={0}
                      rowIndex={props.index}
                    >
                      <Box
                        style={{ ...props.style, paddingBottom: "20px" }}
                        key={`item: ${props.key}`}
                      >
                        {rowRenderer(props)}
                      </Box>
                    </CellMeasurer>
                  );
                }}
              />
            )}
          </AutoSizer>
        </div>
      )}
    </div>
  );
}

export const CVirtualizedList = memo(
  CVirtualizedListComponet
) as typeof CVirtualizedListComponet;
